/**
 * PDP config URL helpers (`coverVariantId`, `selectedOptions`, `mode`, `editkey`).
 * Query `mode=customize` (legacy `mode=custom` still parsed). `mode=set:<id>` for predefined sets.
 */

import {
  attachShopperInvisibleFlags,
  filterVisibleOptionProducts,
  getCoverMaterialName,
  getCoverVariantProductName,
  getOguSlotCatalogProducts,
  getSelectedVariantProduct,
  resolveOptionSlotSelectionName,
} from '@amblique/king-living/app/utils/customize-selections';
import {
  getOptionalAddOns,
  getOptionsGroupingUIProcessed,
  resolveOptionProductShortDescriptionPlain,
} from '@amblique/king-living/app/utils/product-utils';
import {toUrlSearchParams} from '@amblique/king-living/app/utils/url';

export const PDP_CONFIG_COVER_PARAM = 'coverVariantId';
export const PDP_CONFIG_OPTIONS_PARAM = 'selectedOptions';
export const PDP_CONFIG_MODE_PARAM = 'mode';
export const PDP_CONFIG_EDIT_KEY_PARAM = 'editkey';
export const PDP_CONFIG_MODE_CUSTOMIZE = 'customize';

const PDP_CONFIG_MODE_CUSTOMIZE_LEGACY_QUERY = 'custom';

const PDP_CONFIG_MODE_SET_PREFIX = 'set:';

/** SFCC-style product ids — reject injection / oversized query tokens. */
const PDP_CONFIG_PRODUCT_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,79}$/;
const PDP_CONFIG_MAX_OPTION_IDS = 48;

function sanitizePdpConfigProductId(raw) {
  const id = raw != null ? String(raw).trim() : '';

  return PDP_CONFIG_PRODUCT_ID_RE.test(id) ? id : null;
}

function optionIdsMatchMultiset(left, right) {
  if (left.length !== right.length) return false;

  const counts = new Map();

  for (const id of left) {
    counts.set(id, (counts.get(id) || 0) + 1);
  }

  for (const id of right) {
    const next = (counts.get(id) || 0) - 1;

    if (next < 0) return false;

    counts.set(id, next);
  }

  return true;
}

export function parsePdpConfigModeValue(raw) {
  const value = raw != null ? String(raw).trim() : '';

  if (!value) return {pdpMode: null, setProductId: null};

  if (value === PDP_CONFIG_MODE_CUSTOMIZE || value === PDP_CONFIG_MODE_CUSTOMIZE_LEGACY_QUERY) {
    return {pdpMode: PDP_CONFIG_MODE_CUSTOMIZE, setProductId: null};
  }

  if (value.startsWith(PDP_CONFIG_MODE_SET_PREFIX)) {
    const setProductId = sanitizePdpConfigProductId(value.slice(PDP_CONFIG_MODE_SET_PREFIX.length));

    return setProductId ? {pdpMode: 'set', setProductId} : {pdpMode: null, setProductId: null};
  }

  return {pdpMode: null, setProductId: null};
}

export function formatPdpConfigModeValue(pdpMode, setProductId) {
  if (pdpMode === 'set') {
    const id = sanitizePdpConfigProductId(setProductId);

    return id ? `${PDP_CONFIG_MODE_SET_PREFIX}${id}` : null;
  }

  if (pdpMode === PDP_CONFIG_MODE_CUSTOMIZE) {
    return PDP_CONFIG_MODE_CUSTOMIZE;
  }

  return null;
}

function getOguSlotHydrateCandidates(value) {
  if (value?.isHidden === true || value?.isVisible === false) {
    return getOguSlotCatalogProducts(value);
  }

  return filterVisibleOptionProducts(value?.products);
}

function hydrateOguContainerSlots(container, pool) {
  const cid = container?.containerId;

  if (!cid || cid === 'cover') return null;

  const values = Array.isArray(container.values) ? container.values : [];
  const slotSelections = new Array(values.length).fill(null);

  values.forEach((value, idx) => {
    const candidates = getOguSlotHydrateCandidates(value);

    if (candidates.length === 0) return;

    const pick = takeMatchingIdFromPool(candidates, pool);

    if (!pick) return;

    const match = candidates.find((p) => String(p.productId) === pick) ?? candidates[0];
    const slot = buildHydratedOptionSlot(pick, match, idx, container, {
      recommendationName: value.typeName || container.containerName || null,
      recommendationTypeId: value.recommendationTypeId,
    });

    slotSelections[idx] = attachShopperInvisibleFlags(container, value, slot);
  });

  return slotSelections.some((slot) => slot != null) ? slotSelections : null;
}

/** Remove and return the first pool id matching a candidate. Mutates `pool`. */
function takeMatchingIdFromPool(candidates, pool, getId = (p) => p?.productId) {
  if (!Array.isArray(candidates) || candidates.length === 0 || !Array.isArray(pool)) {
    return null;
  }

  const candidateIds = new Set(
    candidates.map((p) => getId(p)).filter(Boolean).map(String)
  );
  const poolIdx = pool.findIndex((id) => candidateIds.has(String(id)));

  if (poolIdx === -1) return null;

  const pick = String(pool[poolIdx]);

  pool.splice(poolIdx, 1);

  return pick;
}

function buildHydratedOptionSlot(pick, match, idx, container, meta = {}) {
  return {
    productId: pick,
    name: resolveOptionSlotSelectionName(match, container) || null,
    shortDescription: resolveOptionProductShortDescriptionPlain(match) || null,
    price: match?.price ?? null,
    recommendationName: meta.recommendationName || null,
    recommendationTypeId: meta.recommendationTypeId,
    slotIndex: idx,
  };
}

function readWindowLocationField(field) {
  if (typeof window === 'undefined') return '';
  const value = window.location?.[field];

  return typeof value === 'string' ? value : '';
}

function readPathAndSearch(location) {
  const locationPathname = typeof location?.pathname === 'string' ? location.pathname : '';

  return {
    pathname: locationPathname || readWindowLocationField('pathname'),
    search: resolvePdpConfigSearch(location),
  };
}

/** Router `search` can be `''` on first paint; empty is not nullish so `??` skips the window URL. */
export function resolvePdpConfigSearch(location) {
  const locationSearch = typeof location?.search === 'string' ? location.search : '';

  return locationSearch || readWindowLocationField('search');
}

/**
 * @param {string|URLSearchParams|null|undefined} search
 * @returns {{coverVariantId: string|null, selectedOptionIds: string[], pdpMode: 'set'|'customize'|null, setProductId: string|null, editKey: string|null}}
 */
export function parsePdpConfigSearchParams(search) {
  const params = toUrlSearchParams(search);
  const coverVariantId = sanitizePdpConfigProductId(params.get(PDP_CONFIG_COVER_PARAM));
  const rawOptions = params.get(PDP_CONFIG_OPTIONS_PARAM);
  const selectedOptionIds =
    rawOptions != null && String(rawOptions).trim() !== '' ?
      String(rawOptions)
        .split(',')
        .map(sanitizePdpConfigProductId)
        .filter(Boolean)
        .slice(0, PDP_CONFIG_MAX_OPTION_IDS) :
      [];
  const {pdpMode, setProductId} = parsePdpConfigModeValue(params.get(PDP_CONFIG_MODE_PARAM));
  const editKey = sanitizePdpConfigProductId(params.get(PDP_CONFIG_EDIT_KEY_PARAM));

  return {coverVariantId, selectedOptionIds, pdpMode, setProductId, editKey};
}

/** True when sanitized config values are present (safe to hydrate). */
export function hasPdpConfigUrlParams(search) {
  const {coverVariantId, selectedOptionIds, pdpMode} = parsePdpConfigSearchParams(search);

  return Boolean(coverVariantId) || selectedOptionIds.length > 0 || pdpMode === 'set' || pdpMode === PDP_CONFIG_MODE_CUSTOMIZE;
}

/** True when raw config query keys exist (even if values fail sanitize). */
export function hasPdpConfigUrlKeys(search) {
  const params = toUrlSearchParams(search);

  return (
    params.has(PDP_CONFIG_COVER_PARAM) ||
    params.has(PDP_CONFIG_OPTIONS_PARAM) ||
    params.has(PDP_CONFIG_MODE_PARAM) ||
    params.has(PDP_CONFIG_EDIT_KEY_PARAM)
  );
}

export function compareVisibleConfigToUrl({
  visibleCoverVariantId,
  visibleOptionIds,
  urlCoverVariantId,
  urlSelectedOptionIds,
}) {
  const urlCover = sanitizePdpConfigProductId(urlCoverVariantId);
  const visibleCover = sanitizePdpConfigProductId(visibleCoverVariantId);

  if (!urlCover || !visibleCover || urlCover !== visibleCover) return false;

  const urlIds = (Array.isArray(urlSelectedOptionIds) ? urlSelectedOptionIds : [])
    .map(sanitizePdpConfigProductId)
    .filter(Boolean);
  const visibleIds = (Array.isArray(visibleOptionIds) ? visibleOptionIds : [])
    .map(sanitizePdpConfigProductId)
    .filter(Boolean);

  return optionIdsMatchMultiset(urlIds, visibleIds);
}

export function hasPdpConfigSelections(selections) {
  if (!selections || typeof selections !== 'object') return false;

  if (selections.cover?.productId) return true;

  for (const [key, value] of Object.entries(selections)) {
    if (key === 'cover' || key === 'optionalAddOns') continue;
    if (!Array.isArray(value)) continue;
    if (value.some((slot) => slot?.productId)) return true;
  }

  const addons = selections.optionalAddOns?.addons;

  return Array.isArray(addons) && addons.some((slot) => slot?.productId);
}

/** Consume pool IDs into optional-add-on slots. Mutates `pool`. KingGuard excluded. */
function hydrateOptionalAddOnsFromPool(product, pool) {
  if (!Array.isArray(pool) || pool.length === 0) return null;

  const slots = getOptionalAddOns(product)
    .map((value) => ({
      value,
      candidates: filterVisibleOptionProducts(value?.products),
    }))
    .filter((slot) => slot.candidates.length > 0);

  if (slots.length === 0) return null;

  const addOnSlots = new Array(slots.length).fill(null);
  let any = false;

  slots.forEach(({value, candidates}, idx) => {
    const pick = takeMatchingIdFromPool(
      candidates,
      pool,
      (p) => p?.productId ?? p?.id
    );

    if (!pick) return;

    const match =
      candidates.find((p) => String(p.productId ?? p.id) === pick) ?? candidates[0];

    addOnSlots[idx] = buildHydratedOptionSlot(pick, match, idx, null, {
      recommendationName: value.typeName || 'optionalAddOns',
      recommendationTypeId: value.recommendationTypeId,
    });
    any = true;
  });

  return any ? {addons: addOnSlots} : null;
}

function appendOptionalAddonProductIds(ids, selections) {
  const addons = selections?.optionalAddOns?.addons;

  if (!Array.isArray(addons)) return;

  for (const slot of addons) {
    if (!slot?.productId) continue;

    const id = sanitizePdpConfigProductId(slot.productId);

    if (id) ids.push(id);
  }
}

/**
 * Hydrate selections from URL. Unmatched ids returned for ephemeral passthrough.
 * KingGuard has no product id — not hydrated.
 */
export function hydrateSelectionsFromUrlParams({
  product,
  coverVariantId = null,
  selectedOptionIds = [],
}) {
  const selections = {};
  let unmatchedCoverVariantId = null;
  const safeCover = sanitizePdpConfigProductId(coverVariantId);
  const safeOptions = Array.isArray(selectedOptionIds) ?
    selectedOptionIds.map(sanitizePdpConfigProductId).filter(Boolean) :
    [];

  if (!product) {
    return {
      selections,
      unmatchedCoverVariantId: safeCover,
      unmatchedOptionIds: safeOptions,
    };
  }

  if (safeCover) {
    const variant = getSelectedVariantProduct(product, safeCover);

    if (variant?.productId) {
      selections.cover = {
        productId: variant.productId,
        variationValues: variant.variationValues || {},
        name: getCoverVariantProductName(variant) || null,
        materialName: getCoverMaterialName(variant) || null,
        salePrice: variant.salePrice ?? null,
        price: variant.price ?? null,
        imageUrl: variant.swatchImageUrl ?? null,
        selectedAt: Date.now(),
      };
    } else {
      unmatchedCoverVariantId = safeCover;
    }
  }

  const pool = [...safeOptions];

  if (pool.length === 0) {
    return {selections, unmatchedCoverVariantId, unmatchedOptionIds: []};
  }

  const containers = getOptionsGroupingUIProcessed(product);

  if (Array.isArray(containers) && containers.length > 0) {
    for (const container of containers) {
      const slotSelections = hydrateOguContainerSlots(container, pool);

      if (slotSelections) {
        selections[container.containerId] = slotSelections;
      }
    }
  }

  const optionalAddOns = hydrateOptionalAddOnsFromPool(product, pool);

  if (optionalAddOns) {
    selections.optionalAddOns = optionalAddOns;
  }

  return {
    selections,
    unmatchedCoverVariantId,
    unmatchedOptionIds: pool,
  };
}

export function validateUrlConfigForCustomMode({
  product,
  coverVariantId = null,
  selectedOptionIds = [],
}) {
  const safeCover = sanitizePdpConfigProductId(coverVariantId);

  if (safeCover) {
    const variant = getSelectedVariantProduct(product, safeCover);

    if (!variant?.productId) return false;
  }

  const {unmatchedOptionIds} = hydrateSelectionsFromUrlParams({
    product,
    coverVariantId,
    selectedOptionIds,
  });

  return unmatchedOptionIds.length === 0;
}

/**
 * Serialize option productIds (OGU walk, then optional add-ons). KingGuard omitted.
 * URL write keeps shopper-invisible SKUs. Set/customize match can omit them so
 * older `mode=set` links (visible IDs only) still match; `selectSet` fills hidden from the set catalog.
 * @param {object} [options]
 * @param {boolean} [options.includeOptionalAddOns=true]
 * @param {boolean} [options.includeShopperInvisible=true] — skip `slot.isHidden` when false
 */
export function serializeSelectedOptionIds(selections, product, options = {}) {
  if (!selections || typeof selections !== 'object') return [];

  const includeOptionalAddOns = options.includeOptionalAddOns !== false;
  const includeShopperInvisible = options.includeShopperInvisible !== false;
  const containers = getOptionsGroupingUIProcessed(product);
  const ids = [];

  const pushSlotId = (slot) => {
    if (!slot?.productId) return;

    const productId = sanitizePdpConfigProductId(slot.productId);

    if (productId) ids.push(productId);
  };

  if (Array.isArray(containers) && containers.length > 0) {
    for (const container of containers) {
      const cid = container?.containerId;

      if (!cid || cid === 'cover') continue;

      const values = Array.isArray(container.values) ? container.values : [];
      const slots = Array.isArray(selections[cid]) ? selections[cid] : [];
      const len = Math.max(values.length, slots.length);

      for (let i = 0; i < len; i++) {
        if (!includeShopperInvisible && slots[i]?.isHidden === true) continue;

        pushSlotId(slots[i]);
      }
    }

    if (includeOptionalAddOns) {
      appendOptionalAddonProductIds(ids, selections);
    }

    return ids;
  }

  for (const [key, value] of Object.entries(selections)) {
    if (key === 'cover' || key === 'optionalAddOns') continue;
    if (!Array.isArray(value)) continue;
    value.forEach(pushSlotId);
  }

  if (includeOptionalAddOns) {
    appendOptionalAddonProductIds(ids, selections);
  }

  return ids;
}

/** Build next search from selections; preserves unrelated query keys. */
export function buildPdpConfigUrl({
  pathname = '',
  search,
  selections,
  product,
  urlPassthrough = null,
  pdpMode = null,
  setProductId = null,
  editKey = null,
} = {}) {
  const params = toUrlSearchParams(search);

  params.delete(PDP_CONFIG_COVER_PARAM);
  params.delete(PDP_CONFIG_OPTIONS_PARAM);
  params.delete(PDP_CONFIG_MODE_PARAM);
  params.delete(PDP_CONFIG_EDIT_KEY_PARAM);

  const coverId = sanitizePdpConfigProductId(
    selections?.cover?.productId || urlPassthrough?.coverVariantId
  );

  if (coverId) {
    params.set(PDP_CONFIG_COVER_PARAM, coverId);
  }

  const optionIds = serializeSelectedOptionIds(selections, product);
  const passthroughOpts = Array.isArray(urlPassthrough?.selectedOptionIds) ?
    urlPassthrough.selectedOptionIds :
    [];
  const seen = new Set(optionIds);

  for (const id of passthroughOpts) {
    const s = sanitizePdpConfigProductId(id);

    if (!s || seen.has(s)) continue;

    seen.add(s);
    optionIds.push(s);
  }

  if (optionIds.length > 0) {
    params.set(PDP_CONFIG_OPTIONS_PARAM, optionIds.join(','));
  }

  const modeValue = formatPdpConfigModeValue(pdpMode, setProductId);

  if (modeValue) {
    params.set(PDP_CONFIG_MODE_PARAM, modeValue);
  }

  const safeEditKey = sanitizePdpConfigProductId(editKey);

  if (safeEditKey) {
    params.set(PDP_CONFIG_EDIT_KEY_PARAM, safeEditKey);
  }

  const qs = params.toString();

  return {
    pathname: pathname || '',
    search: qs ? `?${qs}` : '',
  };
}

export function buildPdpEditUrl(args = {}) {
  return buildPdpConfigUrl(args);
}

/** Strip only PDP config query keys from a path string (pathname + optional search). */
export function stripPdpConfigParamsFromPath(path) {
  if (!path || typeof path !== 'string') return path || '';

  const qIndex = path.indexOf('?');
  const pathname = qIndex >= 0 ? path.slice(0, qIndex) : path;
  const search = qIndex >= 0 ? path.slice(qIndex) : '';
  const cleaned = buildPdpConfigUrl({
    pathname,
    search,
    selections: null,
    product: null,
  });

  return `${cleaned.pathname}${cleaned.search || ''}`;
}

export function clearPdpConfigUrlParams({history, location} = {}) {
  const {pathname, search} = readPathAndSearch(location);
  const livePathname =
    history?.location?.pathname ??
    (typeof window !== 'undefined' ? window.location.pathname : '');

  // Skip replace when already navigated away (e.g. after upsell push).
  if (pathname && livePathname && pathname !== livePathname) return;

  const next = buildPdpConfigUrl({pathname, search, selections: null, product: null});
  const rawSearch = typeof search === 'string' ? search : '';
  const currentSearch = !rawSearch || rawSearch.startsWith('?') ? rawSearch : `?${rawSearch}`;

  if (next.search === currentSearch) return;

  replaceLocationSearch({history, location, pathname: next.pathname, search: next.search});
}

export function syncPdpConfigUrl({
  history,
  location,
  selections,
  product,
  urlPassthrough = null,
  pdpMode = null,
  setProductId = null,
  editKey = null,
} = {}) {
  const {pathname, search} = readPathAndSearch(location);
  const next = buildPdpConfigUrl({
    pathname,
    search,
    selections,
    product,
    urlPassthrough,
    pdpMode,
    setProductId,
    editKey,
  });
  const rawSearch = typeof search === 'string' ? search : '';
  const currentSearch = !rawSearch || rawSearch.startsWith('?') ? rawSearch : `?${rawSearch}`;

  if (next.search === currentSearch) return;

  replaceLocationSearch({
    history,
    location,
    pathname: next.pathname || pathname,
    search: next.search,
  });
}

/** Cancel / discard: restore URL from committed selections, or clear config params. */
export function restorePdpConfigUrlFromSelections({
  history,
  location,
  selections,
  product,
  pdpMode = null,
  setProductId = null,
} = {}) {
  if (hasPdpConfigSelections(selections)) {
    syncPdpConfigUrl({history, location, selections, product, pdpMode, setProductId});
  } else {
    clearPdpConfigUrlParams({history, location});
  }
}

function replaceLocationSearch({history, location, pathname, search}) {
  const nextPathname = pathname || readPathAndSearch(location).pathname;

  if (!nextPathname) return;

  const current = history?.location ?? location ?? null;
  const hash = typeof current?.hash === 'string' ? current.hash : '';
  const state = current?.state;

  if (history && typeof history.replace === 'function') {
    history.replace({pathname: nextPathname, search, hash, state});

    return;
  }

  if (typeof window !== 'undefined' && window.history?.replaceState) {
    window.history.replaceState(
      window.history.state,
      '',
      `${nextPathname}${search || ''}${hash || ''}`
    );
  }
}
